import { createRef, useState } from 'react';
import { Box, FormControl, HStack } from '@chakra-ui/react';
import BucketSelect from 'components/productManager/BucketSelect';
import { useBucketFile } from 'hooks';
import { Button, Checkbox, FormLabel, Input, Text } from 'components/UI';
import { IData, IProductModel } from 'types';
import { useConfirm } from 'contexts';
import ProductFieldContent from 'components/common/ProductFieldContent';
import ProductFieldEditInput from 'components/common/ProductFieldEditInput';
import TextWhite from 'components/common/TextWhite/TextWhite';
import ProductFieldInput from 'components/common/ProductFieldInput';
import FileBucketSelect from 'components/productManager/FileBucketSelect';
import type { FileBucketSelectProps } from 'components/productManager/FileBucketSelect';
import DropDown from 'components/common/Selectable/DropDown';
import inventorVersions from 'constants/inventorVersions.json';
import { checkIfError } from 'utils/helpers';
import { IProductResource } from '../../../../server/types';

type ProductFieldProps = {
  onChange?: (...args: any) => void;
  fileStateProp?: string;
  name: string;
  isZip?: boolean;
  bucketName?: string;
  displayName: string;
  data: object | IProductResource | string | number | readonly string[] | boolean | undefined;
  policy?: string;
  setOverflow?: (arg0?: boolean) => void;
  edit: boolean;
  fieldType: string;
  buttonText?: string;
  showSelect?: boolean;
  uploadDisabled?: boolean;
  placeholder?: string;
  fileBucketSelectProps?: Omit<FileBucketSelectProps, 'onCancel' | 'onChange'>;
  fileLimitMessage?: string;
};

const ProductField = ({
  bucketName,
  buttonText,
  data,
  displayName,
  edit,
  name,
  fieldType,
  uploadDisabled,
  placeholder,
  policy,
  onChange,
  setOverflow,
  showSelect: showSelectInitial,
  fileStateProp,
  fileBucketSelectProps,
  isZip,
  fileLimitMessage,
}: ProductFieldProps) => {
  const [showSelect, setShowSelect] = useState(false);
  const [showFileBucket, setShowFileBucket] = useState(false);
  const [fetchBucketFile] = useBucketFile();
  const [showConfirm] = useConfirm();
  const fileUploader = createRef<HTMLInputElement>();

  const labelId = displayName.replace(/\s+/g, '');
  const onDropDownChange = (_: string, value: string) => {
    const version = Object.keys(inventorVersions).find(
      (key) => inventorVersions[key as keyof typeof inventorVersions] === value
    );

    onChange?.(name, version);
  };

  const renderDropdown = (values: { [x: string]: string }, itemModel: IProductModel, key: string) => (
    <ProductFieldEditInput>
      <DropDown
        selectValue={values[key]}
        overrideStyles={{
          control: {
            bgColor: 'white',
            minWidth: '250px',
          },
        }}
        value={values[key]}
        filterData={itemModel.values}
        setFilteredData={onDropDownChange}
      />
    </ProductFieldEditInput>
  );

  const cancelUpload = () => {
    const file = '';
    const objectName = '';
    onChange?.(fileStateProp, file, name, objectName, isZip, true);
  };

  const onChangeText = (e: { target: { value: string } }) => {
    onChange?.(name, e.target.value);
  };

  const handleFileBucketSelectClick = () => setShowFileBucket(true);

  const handleOverwrite = async (objectName: string) => {
    if (!bucketName) return;

    const fileResult = await fetchBucketFile(bucketName, objectName as IData);
    const fileResultError = checkIfError(fileResult);

    if (!fileResultError) {
      showConfirm({
        onCancel: cancelUpload,
        onConfirm: () => showConfirm(null),
        title: 'This File Already Exists',
        message: (
          <Text>
            <Text as="span" fontWeight="medium">
              {objectName}
            </Text>{' '}
            already exists. Do you want to overwrite?
          </Text>
        ),
      });
    }
  };

  const onChangeFile = async (event: React.ChangeEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    const objectName = file.name;

    handleOverwrite(objectName);

    onChange?.(fileStateProp, file, name, objectName, isZip, true);
  };

  const handleBucketSelectClick = () => {
    setShowSelect(true);
    setOverflow?.(false);
  };

  const onChangeBucket = (value?: string) => {
    onChange?.(name, value);
    setShowSelect(false);
    setOverflow?.(true);
  };

  const renderTextField = (value: string | number | readonly string[] | boolean | undefined) =>
    edit ? (
      <ProductFieldEditInput>
        <ProductFieldInput
          value={value as string}
          placeholder={placeholder ?? 'Enter a value'}
          onChange={onChangeText}
        />
      </ProductFieldEditInput>
    ) : (
      <ProductFieldEditInput>{value}</ProductFieldEditInput>
    );

  const renderCheckboxField = (value?: string | number | readonly string[] | boolean) =>
    edit ? (
      <ProductFieldEditInput>
        <Checkbox
          variant="white"
          name={name}
          isChecked={!!value}
          onChange={(e) => onChange?.(name, e.target.checked)}
          isDisabled
        />
      </ProductFieldEditInput>
    ) : (
      <ProductFieldEditInput>
        <Checkbox name={name} isChecked={!!data} />
      </ProductFieldEditInput>
    );

  const isDisabled = ((name === 'objectName' || name === 'quoteObjectName') && bucketName === '') || uploadDisabled;

  const renderFileSelect = (value?: boolean | React.ReactChild | React.ReactFragment | null) => {
    const renderSelect = showSelectInitial ? (
      <Button size="sm" onClick={() => handleFileBucketSelectClick()} isDisabled={isDisabled} aria-labelledby={labelId}>
        <TextWhite>Select</TextWhite>
      </Button>
    ) : null;

    if (edit) {
      return (
        <ProductFieldEditInput>
          <Box mr={2}>{value}</Box>
          <Button mr={2} size="sm" onClick={() => fileUploader.current?.click()} isDisabled={isDisabled}>
            <TextWhite>Upload</TextWhite>
          </Button>
          {renderSelect}
          <Input
            placeholder="Select a file"
            type="file"
            id="file"
            containerProps={{
              display: 'none',
            }}
            ref={fileUploader}
            display="none"
            onChange={onChangeFile as unknown as React.ChangeEventHandler<HTMLInputElement> | undefined}
          />
        </ProductFieldEditInput>
      );
    }

    return <ProductFieldEditInput>{value}</ProductFieldEditInput>;
  };

  const renderForgeBucketSelect = showSelect ? (
    <BucketSelect onChange={onChangeBucket} policy={policy} onCancel={() => setShowSelect(false)} />
  ) : null;

  const onChangeBucketFile = (value: string) => {
    onChange?.(fileStateProp, null, name, value, isZip, false);
    setShowFileBucket(false);
  };

  const renderFileBucketSelect = () => {
    if (!showFileBucket) return null;

    return (
      <FileBucketSelect
        onChange={onChangeBucketFile}
        bucketName={bucketName}
        onCancel={() => {
          setShowFileBucket(false);
        }}
        isZip={isZip}
        {...fileBucketSelectProps}
      />
    );
  };

  const renderBucketSelect = (value: boolean | React.ReactElement | React.ReactFragment | null | undefined) =>
    edit ? (
      <ProductFieldEditInput>
        <Box mr={2}>{value}</Box>
        <Button size="sm" aria-labelledby={labelId} onClick={handleBucketSelectClick}>
          <TextWhite>{buttonText}</TextWhite>
        </Button>
        {renderForgeBucketSelect}
      </ProductFieldEditInput>
    ) : (
      <ProductFieldEditInput>{value}</ProductFieldEditInput>
    );

  const renderValue = () => {
    let value;

    if (data !== undefined) {
      value = data;
    }
    if (typeof data === 'object') {
      value = JSON.stringify(data);
    }

    switch (fieldType) {
      case 'checkbox':
        return <ProductFieldContent>{renderCheckboxField(value as boolean)}</ProductFieldContent>;
      case 'text':
        return <ProductFieldContent>{renderTextField(value as string)}</ProductFieldContent>;
      case 'fileSelect':
        if (showFileBucket) {
          return <ProductFieldContent>{renderFileBucketSelect()}</ProductFieldContent>;
        }
        return <Box>{renderFileSelect(value as boolean | null)}</Box>;

      case 'bucketSelect':
        return <ProductFieldContent>{renderBucketSelect(value as React.ReactElement | null)}</ProductFieldContent>;
      case 'select':
        return (
          <ProductFieldContent>
            {renderDropdown(
              inventorVersions,
              {
                default: data as string,
                displayName: 'Inventor Version',
                inputType: 'select',
                values: Object.values(inventorVersions),
              },
              data as string
            )}
          </ProductFieldContent>
        );
      default:
        return <ProductFieldContent>{renderTextField(value as string)}</ProductFieldContent>;
    }
  };

  const renderFileLimit = fileLimitMessage ? (
    <>
      &nbsp;-&nbsp;<strong>{fileLimitMessage} Max</strong>
    </>
  ) : null;

  return (
    <HStack
      sx={{
        flex: 1,
        justifyContent: 'space-between',
        bgColor: 'base2Colour.500',
        margin: 0.5,
        p: 2.5,
        pr: 0,
        alignItems: 'center',
        fontSize: 'sm',
        color: 'base2FontColour.500',
      }}
    >
      <FormControl display="flex" alignItems="center" isDisabled={isDisabled}>
        <FormLabel
          id={fieldType === 'bucketSelect' || fieldType === 'fileSelect' ? labelId : undefined}
          sx={{
            fontWeight: 400,
            fontSize: 12,
            flex: 1,
            textTransform: 'uppercase',
            pr: 1.5,
            ml: 0,
            mb: 0,
          }}
        >
          {displayName}
          {renderFileLimit}
        </FormLabel>
        {renderValue()}
      </FormControl>
    </HStack>
  );
};

export default ProductField;
